import type {
  BookingPurchaseDialogData,
  BookingPurchaseDocumentLink,
  BookingPurchaseWarning,
} from './BookingPurchaseDialog.types';
import type { ActionButtonData } from '../action-button/ActionButton';

export type DialogProjectionState = {
  success: boolean;
  error: boolean;
  title: string;
  isUserLogIn: boolean;
  isAllowToBook: boolean;
  showPrice: boolean;
  userHasCredits: boolean;
  isGoingToWaitingList: boolean;
  priceToPay: number | null;
  creditsToPay: number;
  outstandingDocuments: Array<{ name?: string; documentUrl?: string }>;
  errors: string[];
  messagesData: Record<string, unknown>;
  isMissingProfileDetails: boolean;
  showPayment: boolean;
};

function showsPriceRow(state: DialogProjectionState): boolean {
  return !!state.showPrice || !!state.isUserLogIn;
}

function buildNoCreditsPriceLabel(
  state: DialogProjectionState,
  translate: (key: string) => string
): string | null {
  if (state.priceToPay === 0) {
    return translate('WEB_FREE_EVENT');
  }
  if (state.priceToPay === null && state.isUserLogIn) {
    return translate('WEB_NOT_ELIGIBLE');
  }
  return null;
}

export function resolveDialogView(state: {
  success: boolean;
  error: boolean;
}): BookingPurchaseDialogData['view'] {
  if (state.success && !state.error) {
    return 'success';
  }
  if (!state.success && state.error) {
    return 'error';
  }
  return 'main';
}

export function buildPriceLabel(
  state: DialogProjectionState,
  translate: (key: string) => string
): string | null {
  if (!showsPriceRow(state)) {
    return null;
  }
  if (state.userHasCredits) {
    return state.isUserLogIn ? `${state.creditsToPay} ${translate('WEB_CREDITS')}` : null;
  }
  return buildNoCreditsPriceLabel(state, translate);
}

export function buildPriceLabelId(state: DialogProjectionState): string | null {
  if (state.userHasCredits) {
    return null;
  }
  if (state.priceToPay === null && state.isUserLogIn) {
    return 'not-eligible-status-classes';
  }
  return null;
}

function hasOutstandingDocuments(state: DialogProjectionState): boolean {
  return !!state.outstandingDocuments && state.outstandingDocuments.length > 0;
}

function showsWaitingListWarning(state: DialogProjectionState): boolean {
  const priceResolved = state.priceToPay !== null || state.userHasCredits;
  return priceResolved && !!state.isGoingToWaitingList && !hasOutstandingDocuments(state);
}

export function buildWarnings(
  state: DialogProjectionState,
  translate: (key: string) => string,
  handlers: { onOpenProfile?: () => void }
): BookingPurchaseWarning[] {
  const warnings: BookingPurchaseWarning[] = [];

  if (showsWaitingListWarning(state)) {
    warnings.push({
      message: translate('WEB_WAITING_LIST_WARNING'),
      modifier: 'waitlist',
    });
  }
  if (hasOutstandingDocuments(state) && !state.isMissingProfileDetails) {
    warnings.push({
      message: translate('WEB_OUTSTANDING_DOCUMENTS_WARNING'),
      modifier: 'documents',
    });
  }
  if (state.isMissingProfileDetails) {
    warnings.push({
      message: translate('WEB_OUTSTANDING_PROFILE_INFO_WARNING'),
      modifier: 'documents',
      onClick: handlers.onOpenProfile,
    });
  }

  return warnings;
}

export function buildDocumentLinks(
  state: DialogProjectionState,
  translate: (key: string) => string,
  onOpenDocument: (document: { name?: string; documentUrl?: string }) => void
): BookingPurchaseDocumentLink[] {
  if (!state.isUserLogIn) {
    return [];
  }
  if (!state.isAllowToBook) {
    return [];
  }
  if (!hasOutstandingDocuments(state)) {
    return [];
  }

  return state.outstandingDocuments.map((document) => {
    const name = translate(document.name || '');
    return {
      label: `${translate('WEB_SIGN_DOCUMENT')} ${name}`,
      onClick: () => onOpenDocument(document),
    };
  });
}

export function buildResponseTitle(
  view: BookingPurchaseDialogData['view'],
  state: DialogProjectionState,
  translate: (key: string) => string
): string {
  if (view !== 'success') {
    return translate('WEB_YOU_FAILED_BOOKING');
  }
  return translate(
    state.isGoingToWaitingList ? 'YOU_SUCCESSFULLY_JOINED_WAITING_LIST' : 'YOU_SUCCESSFULLY_BOOKED:'
  );
}

export function buildResponseErrors(
  state: DialogProjectionState,
  translate: (key: string, params?: Record<string, unknown>) => string
): string[] {
  return (state.errors || []).map((error) => translate(error, state.messagesData));
}

type FooterButtonArgs = {
  button: 'primary' | 'signUp' | 'done' | 'tryAgain';
  translate: (key: string) => string;
  priceToPay?: number | null;
  userHasCredits?: boolean;
  goingToWaitingList?: boolean;
  transactionInProgress?: boolean;
  isPriceLoading?: boolean;
  isPriceBreakdownLoading?: boolean;
  isMissingProfileDetailsBlockingBooking?: boolean;
  loadingOutstandingDocuments?: boolean;
  isEagreementsFlagResolved?: boolean;
  areDocumentsSigned?: boolean;
  onClick: () => void;
};

function isFooterButtonDisabled(args: FooterButtonArgs): boolean {
  const pending = [
    args.transactionInProgress,
    args.isPriceLoading,
    args.isPriceBreakdownLoading,
    args.isMissingProfileDetailsBlockingBooking,
    args.loadingOutstandingDocuments,
  ].some(Boolean);
  if (args.button !== 'primary') {
    return pending;
  }
  return pending || !args.isEagreementsFlagResolved || !args.areDocumentsSigned;
}

function footerLabelKey(args: FooterButtonArgs): string {
  const buttonLabels: Partial<Record<FooterButtonArgs['button'], string>> = {
    signUp: 'WEB_SIGN_UP_BOOK',
    done: 'WEB_DONE',
    tryAgain: 'WEB_TRY_AGAIN',
  };
  if (buttonLabels[args.button]) {
    return buttonLabels[args.button]!;
  }
  if (args.priceToPay === null && !args.userHasCredits) {
    return 'WEB_NOT_ELIGIBLE_TO_BOOK';
  }
  return args.goingToWaitingList ? 'WEB_JOIN_WAITING_LIST' : 'WEB_BOOK';
}

export function buildBookFooterButton(args: FooterButtonArgs): ActionButtonData {
  const meta: Record<string, { id?: string; dataTestId?: string }> = {
    primary: { id: 'class-booking-confirm' },
    signUp: {},
    done: { id: 'class-booking-done' },
    tryAgain: {},
  };

  const disabled = isFooterButtonDisabled(args);
  const variant = args.button === 'primary' && disabled ? 'disabled' : 'primary';

  return {
    ...meta[args.button],
    label: args.translate(footerLabelKey(args)),
    variant,
    onClick: args.onClick,
  };
}
